import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  StatusSummaryType,
  StatusWidgetDto
} from "@/Pages/Dashboard/shared/DeviceStatus/types";
import useChartElements from "@/Pages/Dashboard/shared/hooks/useChartElements";
import { ROUTES, useMediaQueries } from "@/shared";
import { useChangeSelectedCompanyStore } from "@/stores/useChangeSelectedCompanyStore";
import { useDashboardStore } from "@/stores/useDashboardStore";
import { useDrawerStore } from "@/stores/useDrawerStore";

import { useStyles as dashboardStyles } from "../../../styles";

interface DownloadStatusProps {
  isDownloadStatusLoading: boolean;
  resourceDownloadStatus: StatusWidgetDto | null;
  resourceDownloadLocation: ROUTES;
  setShowBackButton: Dispatch<SetStateAction<boolean>>;
  setBackButton: Dispatch<SetStateAction<Element | null>>;
}

const DownloadStatus = ({
  isDownloadStatusLoading,
  resourceDownloadStatus,
  resourceDownloadLocation,
  setShowBackButton,
  setBackButton
}: DownloadStatusProps) => {
  const isDesktopDrawerOpen = useDrawerStore(
    state => state.isDesktopDrawerOpen
  );

  const isChangeSelectedCompany = useChangeSelectedCompanyStore(
    state => state.isChangeSelectedCompany
  );

  const isDashboardLoading = useDashboardStore(
    state => state.isDashboardLoading
  );

  const { t } = useTranslation();

  const { toSm, to1600, fromLg } = useMediaQueries();

  const {
    generateStatusList,
    generateBackButton,
    generateResourceDownloadsStatusContent
  } = useChartElements({
    t
  });

  // display column height only when desktop drawer is open on 1440px to 1600px screens
  const enlargeListHeight = isDesktopDrawerOpen && to1600 && fromLg;

  const { horizontalChartListBox, horizontalChartContainer } = dashboardStyles({
    toSm,
    fixedContentHeight: true,
    enlargeListHeight: enlargeListHeight
  });

  const isLoading = isDashboardLoading || isDownloadStatusLoading;

  const [showSingleStatusInfo, setShowSingleStatusInfo] =
    useState<StatusSummaryType | null>(null);

  const [hoveredListItem, setHoveredListItem] = useState<string | null>(null);
  const [hoveredElement, setHoveredElement] = useState<number | null>(null);
  const [hovered, setHovered] = useState<boolean>(false);

  useEffect(() => {
    setShowBackButton?.(Boolean(showSingleStatusInfo));
    // set back button when single status info is shown in Connectivity card
    Boolean(showSingleStatusInfo) &&
      setBackButton?.(generateBackButton(handleGoBack) as any);
    !hovered && setHoveredElement(null);
  }, [
    hovered,
    hoveredElement,
    showSingleStatusInfo,
    resourceDownloadStatus,
    setShowBackButton,
    setBackButton,
    generateBackButton
  ]);

  useEffect(() => {
    // close single status info when loading
    isLoading && setShowSingleStatusInfo(null);
  }, [isLoading]);

  useEffect(() => {
    isChangeSelectedCompany && handleGoBack();
  }, [isChangeSelectedCompany]);

  const generateList = (deviceStatus: any) =>
    generateStatusList({
      statusData: deviceStatus,
      hoveredElement,
      hovered,
      hoveredListItem,
      setShowSingleStatusInfo,
      setHoveredListItem
    });

  const showSingleChartItemInfo =
    resourceDownloadStatus && showSingleStatusInfo && !isLoading;
  const showListItemsInfo = resourceDownloadStatus && !isLoading;

  const handleGoBack = () => {
    setShowSingleStatusInfo(null);
    setHoveredListItem(null);
    setHoveredElement(null);
    setHovered(false);
  };

  const statusContent = generateResourceDownloadsStatusContent({
    resourceDownloadLocation,
    isLoading,
    showSingleChartItemInfo,
    showListItemsInfo,
    chartContainer: horizontalChartContainer,
    statusData: resourceDownloadStatus,
    showSingleStatusInfo,
    chartListBox: horizontalChartListBox,
    setHovered,
    setHoveredElement,
    setShowSingleStatusInfo,
    generateList
  });

  return <>{statusContent}</>;
};

export default React.memo(DownloadStatus);
